import styled from '@emotion/styled';
import { css } from '@emotion/react';
import tokens from '@vaa-component-lib/shared.design-tokens/json/variables.json';
import { rgba } from 'polished';

import { StyledScrollbars, NoGapSupport } from '../../lib/utils/mixins';

import { BreakPointToken } from '../../hooks/useMediaQuery';

import { PopOverType, PopOverAlignment } from './pop-over.component';

import { Z_INDICES } from '../../lib/utils/constants';

interface PopOverPositionerProps {
	isBlock?: boolean;
	alignment?: PopOverAlignment;
}

interface PopOverPanelProps {
	type: PopOverType;
	isAnimated?: boolean;
	maxHeight?: boolean;
	variableHeight?: boolean;
	minWidth?: number;
}

interface PopOverInnerPanelProps {
	scroll?: boolean;
	isOverflow?: boolean;
}

const timing = '0.2s';

const {
	spacing: {
		size_4px: { height: size_4 },
		size_8px: { height: size_8 },
		size_12px: { height: size_12 },
		size_16px: { height: size_16 },
		size_20px: { height: size_20 },
		size_24px: { height: size_24 },
		size_32px: { height: size_32 },
		size_40px: { height: size_40 }
	},
	color: colours
} = tokens;

const getAlignmentStyles = (alignment: PopOverAlignment) => {
	switch (alignment) {
		case PopOverAlignment.Right:
			return `
				left: auto;
				right: 0;
      `;
		case PopOverAlignment.Center:
			return `
				left: 50%;
				right: unset;
				position: absolute;
				transform: translateX(-50%) translateY(0);
				`;
		case PopOverAlignment.Left:
			return `
				left: auto;
				right: auto;
      `;
		default:
			return '';
	}
};

export const PopOverPositioner = styled.div<PopOverPositionerProps>`
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	display: ${({ isBlock }) => (isBlock ? 'block' : 'none')};
	pointer-events: ${({ isBlock }) => (isBlock ? 'auto' : 'none')};
	z-index: ${Z_INDICES.popover};
	transform: translate3d(0, 0, 0);

	@media (min-width: ${BreakPointToken.MdMin}px) {
		position: absolute;
		bottom: auto;
		${({ alignment }) => getAlignmentStyles(alignment)}
	}
`;

export const PopOverPanel = styled.section<PopOverPanelProps>`
	border-radius: ${size_12}px ${size_12}px 0 0;
	background: ${colours.light.background_primary};
	transition:
		transform ${timing} ease-in,
		opacity ${timing} ease-in,
		box-shadow ${timing} ease-in;
	overflow: hidden;

	${({ variableHeight }) => variableHeight && `max-height: 100vh;`}

	${({ maxHeight }) =>
		maxHeight
			? `
			max-height: 100vh;
			height: calc(var(--calculated-vh) * 100);
		`
			: `height: unset`};

	padding: ${({ type }) =>
		type === PopOverType.Selector ? `${size_16}px 0` : `${size_16}px`};
	margin: 0;

	opacity: ${({ isAnimated }) => (isAnimated ? '1' : '0')};
	transform: ${({ isAnimated }) =>
		isAnimated ? `translateY(0)` : `translateY(101%)`};
	display: flex;
	flex-direction: column;

	@media (min-width: ${BreakPointToken.MdMin}px) {
		height: auto;
		border-radius: ${size_12}px;
		box-shadow: ${({ isAnimated }) =>
			`0 ${size_4}px ${size_16}px 0 ${rgba(colours.light.neutral_100, isAnimated ? 0.16 : 0)}`};
		margin: ${size_8}px 0 0;
		padding: ${({ type }) =>
			type === PopOverType.Selector
				? `${size_12}px 0 `
				: `${size_20}px ${size_16}px`};
		transform: ${({ isAnimated }) =>
			isAnimated ? `translateY(0)` : `translateY(${-1 * size_20}px)`};
		${({ minWidth }) => minWidth && `min-width: ${minWidth}px`};
	}
`;

export const PopOverInnerPanel = styled.section<PopOverInnerPanelProps>`
	height: 100%;
	overflow-y: ${({ isOverflow }) => (isOverflow ? `auto` : `visible`)};

	@media (min-width: ${BreakPointToken.MdMin}px) {
		overflow-y: visible;
	}

	${({ scroll }) =>
		scroll &&
		`
			@media (min-width: ${BreakPointToken.MdMin}px) {
				max-height: ${10 * size_40}px;
				overflow: auto;
				margin-right: ${size_8}px;
			}

		`};
	${StyledScrollbars}
`;

export const PopOverHeader = styled.header<PopOverPanelProps>`
	display: flex;
	min-height: ${size_32}px;
	gap: ${size_16}px;
	justify-content: space-between;
	align-items: flex-start;
	padding: ${({ type }) =>
		type === PopOverType.Selector
			? `0 ${size_16}px ${size_16}px`
			: `0 0 ${size_16}px`};

	.button-component {
		${NoGapSupport(
			() => css`
				margin: 0 0 0 ${size_16}px;
			`
		)}
	}
`;

export const PopOverFooter = styled.footer<PopOverPanelProps>`
	display: flex;
	min-height: ${size_32}px;
	padding: ${size_16}px 0 0;
	gap: ${size_16}px;
	justify-content: space-between;
	align-items: center;
	margin-top: ${size_24}px;
	position: relative;

	&:before {
		content: '';
		position: absolute;
		background-color: ${colours.light.border_minimal};
		width: calc(100% + ${size_32}px);
		height: 1px;
		margin-left: -${size_16}px;
		top: 0;
	}

	.button-component {
		${NoGapSupport(
			() => css`
				margin: 0 0 0 ${size_16}px;
			`
		)}
	}
	@media (min-width: ${BreakPointToken.MdMin}px) {
		margin: 0;
		padding: 0;
		flex-direction: column;

		&:before {
			display: none;
		}
	}
`;

export const PopOverButton = styled.div`
	margin-left: auto;
`;

export const PopOverFeedbackPanel = styled.div`
	padding: ${size_16}px;
	display: grid;
	grid-template-columns: min-content 1fr;
	gap: ${size_8}px;
	color: ${colours.light.text_error};
	max-width: 375px;
`;
